/* eslint-disable max-lines */
import GoalType, { isValid as isValidGoalType } from "@/constants/GoalType";
import { i18n } from "@/i18n";
import orderBy from "lodash/orderBy";
import reduce from "lodash/reduce";
import uniq from "lodash/uniq";
import { isSame } from "@/lib/nodash";

import uuid from "uuid/v4";
import {
	GoalTypeSelectedEvent
} from "@/telemetry";

const createEmptyMetadata = () => {
	/**
	 * @type InitialGoalMetadata
	 */
	return {
		goalCounts: {
			[ GoalType.ACTIVE ]: 0,
			[ GoalType.ARCHIVED ]: 0,
			[ GoalType.DRAFT ]: 0,
		},
	};
};

/**
 * Reactions are really just mutations that are called when an
 *   action invokes the store's `commit()` function. Separating them
 *   here for clarity. They are merged into mutations below.
 */
const goalSaved = ( state, savedGoal ) => {
	const myGoals = state.myGoals.filter( goal => goal.id !== savedGoal.id );
	myGoals.push( { ...savedGoal, } );
	state.myGoals = myGoals;
};

function updateGoals( { state, myFetchedGoals, } ) {
	const goalIndexMap = {};
		state.myGoals.forEach( ( goal, index ) => {
			goalIndexMap[ goal.id ] = index;
		} );
		const myPatchedGoals = [ ...state.myGoals, ];
		// "patch" goals that are still in the store, but for which we've
		// fetched new data
		myFetchedGoals.forEach( goal => {
			if ( goal.id in goalIndexMap ) {
				myPatchedGoals.splice(
					goalIndexMap[ goal.id ],
					1,
					goal
				);
			} else {
				myPatchedGoals.push( goal );
			}
		} );
		state.myGoals = myPatchedGoals;
}

const computeDuplicateKeyResultErrors = state => {
	const duplicateErrors = {};
	const uiKeys = Object.keys( state.editingKeyResultsMap );
	for ( const outerKey of uiKeys ) {
		if ( outerKey in duplicateErrors ) {
			// eslint-disable-next-line no-continue
			continue;
		}
		const outerResult = state.editingKeyResultsMap[ outerKey ];
		for ( const innerKey of uiKeys ) {
			if ( innerKey === outerKey ) {
				// eslint-disable-next-line no-continue
				continue;
			}
			const innerResult = state.editingKeyResultsMap[ innerKey ];
			const isTitleEqual = outerResult.name.toLowerCase() === innerResult.name.toLowerCase();
			const isOwnerEqual = isSame(
				outerResult.owners.map( owner => owner.personId ),
				innerResult.owners.map( owner => owner.personId )
			);
			if ( isTitleEqual && isOwnerEqual ) {
				duplicateErrors[ outerKey ] = true;
				duplicateErrors[ innerKey ] = true;
				break;
			}
		}
	}
	return duplicateErrors;
};

export default {
	clearGoals: state => {
		// flags
		state.isGoalDialogOpen = false;
		state.isSavingGoal = false;
		state.isSelectedGoalTeamChanging = false;
		state.isInitialLoadComplete = false;
		// metadata
		state.initialMetadata = createEmptyMetadata();
		// goals
		state.selectedGoalTeam = {};
		state.fetchedGoalTypes = [];
		state.myGoals = [];
		state.selectedGoalType = GoalType.ACTIVE;
		state.editingGoalId = "";
		// key results
		state.keyResultsMap = {};
		state.editingKeyResultsOrder = [];
		state.editingKeyResultsMap = {};
		state.deletingKeyResultsList = [];
		state.keyResultVisibilityMap = {};
		state.keyResultFetchStatusMap = {};
		state.keyResultFetchErrors = {};
		state.examinedGoal = null;
		state.currentOffset = 0;
		state.currentLimit = 20;
		state.noMoreGoalsForType = false;
		state.sortDirection = "asc";
		state.selectedTeamId = null;
		state.selectedMeetingId = null;
	},
	closeGoalDialog: state => {
		state.isGoalDialogOpen = false;
		state.editingKeyResultsOrder = [];
		state.editingKeyResultsMap = {};
		state.deletingKeyResultsList = [];
		state.selectedGoalTeam = {};
		state.editingGoalId = null;
	},
	editingGoal: ( state, { goalId, keyResults, } ) => {
		state.editingGoalId = goalId;
		state.editingKeyResultsMap = {};
		state.editingKeyResultsOrder = [];
		const orderedKeyResults = orderBy( keyResults, [ "sortOrder", ], [ "asc", ] );
		orderedKeyResults.forEach( keyResult => {
			const uiKey = uuid();
			state.editingKeyResultsMap[ uiKey ] = { ...keyResult, };
			state.editingKeyResultsOrder.push( uiKey );
		} );
		state.isGoalDialogOpen = true;
		state.duplicateKeyResultErrors = {};
	},
	goalExamined: ( state, goalId ) => {
		state.examinedGoal = goalId;
	},
	goalDeleted: ( state, goalId ) => {
		state.myGoals = [ ...state.myGoals.filter( goal => goal.id !== goalId ), ];
		delete state.keyResultsMap[ goalId ];
	},
	goalSaved,
	goalTeamSelected: ( state, team ) => {
		state.selectedGoalTeam = team;
	},
	gokrMetadataLoaded: ( state, { initialMetadata, } ) => {
		state.initialMetadata = {
			...state.initialMetadata,
			...initialMetadata,
			...{
				// easier to de-reference with enum
				goalCounts: {
					[ GoalType.ACTIVE ]: initialMetadata.activeGoalCount,
					[ GoalType.DRAFT ]: initialMetadata.draftGoalCount,
					[ GoalType.ARCHIVED ]: initialMetadata.archivedGoalCount,
				},
		}, };
	},
	keyResultDeleted: ( state, { goalId, keyResultId, } ) => {
		state.keyResultsMap[ goalId ] = [ ...state.keyResultsMap[ goalId ].filter( kr => kr.id !== keyResultId ), ];
	},
	keyResultsFetched: ( state, keyResultsMap ) => {
		state.keyResultsMap = { ...state.keyResultsMap, ...keyResultsMap, };
	},
	keyResultFetchFailed: ( state, { goalIds, message, } ) => {
		const keyResultFetchErrors = { ...state.keyResultFetchErrors, };
		goalIds.forEach( goalId => {
			keyResultFetchErrors[ goalId ] = i18n.t( "gokr.GoalsStore_keyResults_keyResultFetchFailed", { message, } );
		} );
		state.keyResultFetchErrors = keyResultFetchErrors;
	},
	keyResultSaved: ( state, { uiKey, keyResult, } ) => {
		const { goalId, } = keyResult;
		if ( state.editingGoalId === goalId ) {
			state.editingKeyResultsMap = { ...state.editingKeyResultsMap, [ uiKey ]: keyResult, };
		}
		const goalKeyResults = [ ...( state.keyResultsMap[ goalId ] || [] ), ];
		const keyResultIndex = goalKeyResults.findIndex( goalKeyResult => goalKeyResult.id === keyResult.id );
		if ( keyResultIndex < 0 ) {
			goalKeyResults.push( keyResult );
		} else {
			goalKeyResults[ keyResultIndex ] = keyResult;
		}
		state.keyResultsMap = { ...state.keyResultsMap, [ goalId ]: goalKeyResults, };
	},
	hideAllKeyResults: state => {
		state.keyResultVisibilityMap = {
			...(
				reduce( state.keyResultVisibilityMap, ( accum, value, key ) => {
					accum[ key ] = false;
					return accum;
				}, {} )
			),
		};
	},
	incrementPagingOffset: state => {
		state.currentOffset += state.currentLimit;
	},
	loadedGoal: goalSaved,
	myGoalsLoaded: ( state, { goalType, myGoals: myFetchedGoals, } ) => {
		state.fetchedGoalTypes = uniq( [ ...state.fetchedGoalTypes, goalType, ] );
		state.isResortingGoals = false;
		updateGoals( { state, myFetchedGoals, } );
	},
	openGoalDialog: state => {
		state.isGoalDialogOpen = true;
	},
	queueNewKeyResult: state => {
		const queuedKeyResults = { ...state.editingKeyResultsMap, };
		const uiKey = uuid();
		queuedKeyResults[ uiKey ] = {
			confidence: "",
			currentValue: "",
			goalValue: "",
			name: "",
			owners: [],
			type: "",
		};
		state.editingKeyResultsMap = queuedKeyResults;
		state.editingKeyResultsOrder.push( uiKey );
	},
	removeKeyResult: ( state, uiKeyToRemove ) => {
		const krId = state.editingKeyResultsMap[ uiKeyToRemove ].id;
		if ( krId ) {
			state.deletingKeyResultsList.push( krId );
		}
		delete state.editingKeyResultsMap[ uiKeyToRemove ];
		state.editingKeyResultsOrder = [ ...state.editingKeyResultsOrder, ].filter( uiKey => {
			return uiKey !== uiKeyToRemove;
		} );
		state.duplicateKeyResultErrors = computeDuplicateKeyResultErrors( state );
	},
	replaceKeyResult: ( state, { uiKey, keyResult, } ) => {
		state.editingKeyResultsMap[ uiKey ] = { ...keyResult, };
		state.duplicateKeyResultErrors = computeDuplicateKeyResultErrors( state );
	},
	resetGoalFetchPolicy: state => {
		state.selectedGoalType = GoalType.ACTIVE;
		state.fetchedGoalTypes = [];
	},
	setGoalFilterCriteria: ( state, { teamId = null, meetingId = null, goalType = GoalType.ACTIVE, } ) => {
		state.selectedTeamId = teamId;
		state.selectedMeetingId = meetingId;
		state.selectedGoalType = goalType;
	},
	setInitialLoadComplete: state => {
		state.isInitialLoadComplete = true;
	},
	setIsSelectedGoalTeamChanging: ( state, isChanging ) => {
		state.isSelectedGoalTeamChanging = !!isChanging;
	},
	setNoMoreGoalsForType: state => {
		state.noMoreGoalsForType = true;
	},
	setSelectedGoalType: ( state, { goalType, suppressTelemetry, } = { suppressTelemetry: false, } ) => {
		if ( !isValidGoalType( goalType ) ) {
			goalType = GoalType.ACTIVE;
		}
		state.selectedGoalType = goalType;
		state.currentOffset = 0;
		state.currentLimit = 20;
		state.noMoreGoalsForType = false;
		if ( !suppressTelemetry ) {
			const event = new GoalTypeSelectedEvent( goalType );
			event.submit();
		}
	},
	setSelectedTeamId: ( state, teamId ) => {
		state.selectedTeamId = teamId;
	},
	setSortDirection: ( state, sortDirection ) => {
		state.sortDirection = sortDirection;
		state.isResortingGoals = true;
	},
	toggleGoalDialog: ( state, isOpen ) => {
		state.isGoalDialogOpen = !!isOpen;
		if ( !isOpen ) {
			state.editingKeyResultsOrder = [];
			state.editingKeyResultsMap = {};
			state.deletingKeyResultsList = [];
			state.selectedGoalTeam = {};
			state.editingGoalId = null;
		}
	},
	toggleIsLoadingGoals: ( state, isLoading ) => {
		state.isLoadingGoals = isLoading;
	},
	toggleKeyResultsBeingFetched: ( state, { goalIds, inProgress, } ) => {
		const keyResultFetchStatusMap = { ...state.keyResultFetchStatusMap, };
		goalIds.forEach( goalId => {
			keyResultFetchStatusMap[ goalId ] = inProgress;
		} );
		state.keyResultFetchStatusMap = keyResultFetchStatusMap;
	},
	toggleKeyResultVisibility: ( state, { goalId, isVisible, } ) => {
		state.keyResultVisibilityMap = {
			...state.keyResultVisibilityMap,
			...{ [ goalId ]: isVisible, },
		};
	},
	toggleSavingGoal: ( state, isSaving ) => {
		state.isSavingGoal = isSaving;
	},
	updatedGoalLoaded: ( state, { goal, } ) => {
		updateGoals( { state, myFetchedGoals: [ goal, ], } );
	},
};



// WEBPACK FOOTER //
// ./src/store/modules/goals/mutations.js